import React, { useMemo } from "react";
import { AppHeader } from "./AppHeader";
import { Sidebar } from "./Sidebar";
import SkyToggle from "../ui/sky-toggle";
import { useBillingContext } from "@/contexts/BillingContext";
// import { RemainingQuotesPill } from "@/components/billing/RemainingQuotesPill"; // BILLING DISABLED

type AppLayoutProps = {
    children: React.ReactNode;
    sideNavRemoveIds?: string[];
    sideNavIncludedIds?: string[];
    showOrgSwitcher?: boolean;
};

/**
 * @purpose The app shell component contain sidebar nav info and the main content of the app
 * to add a new component in app shell and use it in the `AppShell` component it will apply to all the app pages
 *
 * @param children the main content of the app
 * @param sideNavIncludedIds the ids of the sidebar nav items to include in the sidebar specifically
 * @param sideNavRemoveIds the ids of the sidebar nav items to remove from the sidebar specifically
 *
 */

export const AppLayoutShell = React.memo(function AppLayoutShell({
    children,
    sideNavIncludedIds,
    sideNavRemoveIds,
    showOrgSwitcher,
}: AppLayoutProps) {
    const { quota, loading, planName, planStatus, billingStatus } = useBillingContext();
    const normalizedStatus = planStatus?.replace(/_/g, ' ') ?? null;
    const isTrialPlan = planStatus === 'trial';
    const trialInfo = billingStatus?.trial_info;
    const trialDaysRemaining = typeof trialInfo?.days_remaining === 'number' ? trialInfo.days_remaining : null;
    const trialExpired = trialInfo?.expired === true;

    const statusSuffix = useMemo(() => {
        const trialSuffix = (() => {
            if (trialExpired) {
                return 'Trial expired';
            }
            if (trialDaysRemaining !== null) {
                if (trialDaysRemaining <= 0) {
                    return 'Ends today';
                }
                const plural = trialDaysRemaining === 1 ? 'day' : 'days';
                return `${trialDaysRemaining} ${plural} left`;
            }
            return 'In progress';
        })();
        return isTrialPlan ? trialSuffix : normalizedStatus;
    }, [trialExpired, trialDaysRemaining, isTrialPlan, normalizedStatus]);

    return (
        <div className="min-h-screen bg-background">
            <div className="flex h-screen">
                <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
                    <Sidebar
                        sidebarNavIncludeIds={sideNavIncludedIds}
                        sidebarNavRemoveIds={sideNavRemoveIds}
                        showOrgSwitcher={showOrgSwitcher}
                    />
                </div>
                
                <div className="lg:pl-72 flex flex-col flex-1 min-h-0">
                    {/* Mobile Header */}
                    <div className="sticky top-0 z-40 lg:hidden">
                        <AppHeader
                            showOrgSwitcher={showOrgSwitcher}
                            sidebarNavIncludeIds={sideNavIncludedIds}
                            sidebarNavRemoveIds={sideNavRemoveIds}
                            quota={quota}
                            quotaLoading={loading}
                            planName={planName}
                            planStatus={planStatus}
                        />
                    </div>
                    
                    {/* Desktop Header */}
                    <div className="hidden lg:block sticky top-0 z-40 bg-background">
                        <div className="flex h-16 items-center justify-end gap-3 border-b border-border px-6">
                            {planName && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-muted/70 px-3 py-1 text-xs font-medium text-muted-foreground">
                                    <span className="text-foreground">{planName}</span>
                                    {statusSuffix && (
                                        <span className="flex items-center gap-1 text-xs capitalize opacity-80">
                                            <span aria-hidden className="opacity-40">•</span>
                                            <span>{statusSuffix}</span>
                                        </span>
                                    )}
                                </span>
                            )}
                            {/* <RemainingQuotesPill quota={quota} loading={loading} /> */}
                            <SkyToggle />
                        </div>
                    </div>
                    
                    <main className="flex-1 overflow-auto">
                        {children}
                    </main>
                </div>
            </div>
        </div>
    );
});
